import { TimeAllocationType, type TimeAllocation } from '../../../../api/work-periods-client/work-periods-client.type';
import { formatDate, formatMonthDate, getInclusiveISOEndDate } from '../../../../helpers/date-time-helpers';
import type { ChartDataItem, TimeAllocationChartLabels } from './line-chart.type';

/**
 * Check if a measure value should be displayed in the tooltip
 *
 * @param {number | null} value - The measure value
 * @returns {boolean} - Whether the value should be displayed
 */
const shouldDisplayValue = (value: number | null): boolean => {
  return value !== null && value !== undefined;
};

/**
 * Check if a chart item has any data across all data points
 * An item is considered to have no data if:
 * 1. The itemId property doesn't exist on any data points, OR
 * 2. All values for the itemId are null or undefined
 *
 * @param {ChartDataItem[]} chartData - The chart data array
 * @param {string} itemId - The item ID to check for data
 * @returns {boolean} - Whether the item has any non-null data
 */
const hasDataForItem = (chartData: ChartDataItem[], itemId: string): boolean => {
  if (chartData.length === 0) {
    return false;
  }

  return chartData.some((dataPoint) => {
    const value = dataPoint[itemId];
    return value !== null && value !== undefined;
  });
};

/**
 * Returns an inclusive YYYY-MM-DD end date for a time allocation (for labeling).
 */
const getInclusiveAllocationEndDate = (allocation: TimeAllocation): string => {
  return getInclusiveISOEndDate(allocation.end) ?? allocation.end.split('T')[0] ?? '';
};

/**
 * Builds display labels for a time-allocation bucket.
 *
 * Backend time series keys represent `time_allocation_id` which is the bucket **start** date.
 * That can look like data "stops early" when the selected range ends mid-bucket (partial trailing bucket).
 * This helper keeps the X-axis semantics intuitive (bucket starts) while making the final tick match the selected end date,
 * and ensures tooltips always show the full bucket window. Applies to every allocation type — for daily the bucket start
 * already equals its inclusive end, so the last-tick override is a no-op there.
 *
 * @param {string} rawDate - The bucket key (its start date), matching a `timeAllocations[].id`
 * @param {TimeAllocationType} timeAllocationType - Active granularity; selects the default formatter (monthly → month/year)
 * @param {TimeAllocation[]} timeAllocations - All buckets for the selected range; the last one's end date drives the final tick
 * @param {(date: string) => string} [formatDateLabel] - Overrides the default formatter (e.g. a compact `MMM D`); when omitted, monthly uses month/year and everything else uses full dates
 * @returns {TimeAllocationChartLabels} The tick label (axis) and tooltip label (full window)
 */
const getTimeAllocationChartLabels = ({
  rawDate,
  timeAllocationType,
  timeAllocations,
  formatDateLabel,
}: {
  rawDate: string;
  timeAllocationType: TimeAllocationType;
  timeAllocations: TimeAllocation[];
  formatDateLabel?: (date: string) => string;
}): TimeAllocationChartLabels => {
  const formatter =
    formatDateLabel ?? (timeAllocationType === TimeAllocationType.Monthly ? formatMonthDate : formatDate);
  const fallback = formatter(rawDate);
  // With no matching allocation, the bucket key IS the only date we have, so
  // both inclusive bounds collapse to it (date-only, timezone-independent).
  const rawDatePart = rawDate.split('T')[0] ?? rawDate;

  if (!timeAllocations?.length) {
    return { tickLabel: fallback, tooltipLabel: fallback, startDate: rawDatePart, endDate: rawDatePart };
  }

  const allocation = timeAllocations.find((a) => a.id === rawDate);
  if (!allocation) {
    return { tickLabel: fallback, tooltipLabel: fallback, startDate: rawDatePart, endDate: rawDatePart };
  }

  const startDate = allocation.start.split('T')[0] ?? rawDatePart;
  const endDate = getInclusiveAllocationEndDate(allocation);

  const startLabel = formatter(startDate);
  const endLabel = formatter(endDate);

  // Keep the X-axis semantics as "bucket start" (the backend key) so the first tick matches the user's start date.
  // Only override the LAST bucket's tick label to show the selected end date (bucket end) to avoid the "stops early"
  // confusion on partial trailing buckets. This is intentionally allocation-type-agnostic: monthly ranges hit the same
  // partial-trailing-bucket problem as weekly/biweekly, and daily is a natural no-op (start === inclusive end).
  let maxEndDate: string | null = null;
  for (const a of timeAllocations) {
    const d = getInclusiveAllocationEndDate(a);
    if (!d) continue;
    if (maxEndDate === null || d > maxEndDate) {
      maxEndDate = d;
    }
  }
  const isLastBucket = Boolean(maxEndDate && endDate === maxEndDate);

  return {
    tickLabel: isLastBucket ? endLabel : startLabel,
    tooltipLabel: startLabel === endLabel ? endLabel : `${startLabel} – ${endLabel}`,
    startDate,
    endDate,
  };
};

export { getTimeAllocationChartLabels, hasDataForItem, shouldDisplayValue };
